import { TextField, Select, SelectEvent } from "@king-ict/eupisi-ui/components";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { AlternativeDocumentInfo } from "./AlternativeDocumentInfo";
import { FetchedDiffValue } from "./index";
import { FormCard, InfoPanel } from "../../../../components";
import { SectionHeader } from "../../../../components/FormControls";
import { AddressDisplay } from "../../../../components/FormControls/AddressDisplay";
import { GenderEnum, PersonIdentification } from "../../../../models";
import { PersonDetailsWithNullPerson } from "../../../../models/Request/RegistrationFormDataChange";
import { usePersonOib, useParentCodebooks } from "../hooks";

interface IProps {
  parentDetails: PersonDetailsWithNullPerson;
  applicantParent?: PersonIdentification;
  onUpdate: (updatedDetails: PersonDetailsWithNullPerson) => void;
}

export const ApplicantParentAccordion = ({
  parentDetails,
  applicantParent,
  onUpdate,
}: IProps) => {
  const { t } = useTranslation();
  const { parentshipTypes, countries, citizenships, getCodebookName } =
    useParentCodebooks();
  const {
    isLoading,
    fetchPersonDetailsByOib,
    canApply,
    applyFetchedData,
    data: fetchedData,
    successMessage,
  } = usePersonOib();

  const userOib = parentDetails.person?.oib ?? applicantParent?.oib ?? "";

  const handleRefresh = () => {
    if (!userOib) return;

    fetchPersonDetailsByOib(userOib);
  };

  const handleApply = () => {
    const fetched = applyFetchedData();
    if (fetched) {
      const updated: PersonDetailsWithNullPerson = {
        ...fetched,
        parentshipTypeId: parentDetails.parentshipTypeId,
        qualificationName: parentDetails.qualificationName,
        phoneNumber: parentDetails.phoneNumber,
        email: parentDetails.email,
      };

      onUpdate(updated);
    }
  };

  const handleUpdate = (value: Partial<PersonDetailsWithNullPerson>) => {
    onUpdate({ ...parentDetails, ...value });
  };

  return (
    <FormCard className="position-relative">
      {successMessage && (
        <InfoPanel color="success" fullWidth>
          {t(successMessage)}
        </InfoPanel>
      )}
      <SectionHeader
        title={t("person.personalInformation")}
        actionButton={{
          canApply,
          handleApply,
          handleRefresh,
          isLoading,
          isHidden: !userOib,
        }}
      />
      <AlternativeDocumentInfo
        personIdentification={applicantParent}
        userOib={userOib}
      />
      <Row className="mb-2">
        <Col md={3}>
          <TextField
            label={t("person.firstName")}
            value={parentDetails.firstName}
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={
              canApply &&
              !!fetchedData &&
              fetchedData.firstName !== parentDetails.firstName
            }
          >
            {fetchedData?.firstName}
          </FetchedDiffValue>
        </Col>
        <Col md={3}>
          <TextField
            label={t("person.lastName")}
            value={parentDetails.lastName}
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={
              canApply &&
              !!fetchedData &&
              fetchedData.lastName !== parentDetails.lastName
            }
          >
            {fetchedData?.lastName}
          </FetchedDiffValue>
        </Col>
        <Col md={3}>
          <TextField
            label={t("person.oib")}
            value={userOib}
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={
              canApply && !!fetchedData && fetchedData.person?.oib !== userOib
            }
          >
            {fetchedData?.person?.oib}
          </FetchedDiffValue>
        </Col>
        <Col md={3}>
          <TextField
            label={t("person.dateOfBirth")}
            value={
              parentDetails.dateOfBirth
                ? new Date(parentDetails.dateOfBirth).toLocaleDateString()
                : ""
            }
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={
              canApply &&
              !!fetchedData &&
              fetchedData.dateOfBirth !== parentDetails.dateOfBirth
            }
          >
            {fetchedData?.dateOfBirth
              ? new Date(fetchedData.dateOfBirth).toLocaleDateString()
              : ""}
          </FetchedDiffValue>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col md={3}>
          <TextField
            label={t("person.sex")}
            value={
              parentDetails.genderId === GenderEnum.Male
                ? t("gender.Male")
                : t("gender.Female")
            }
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={
              canApply &&
              !!fetchedData &&
              fetchedData.genderId !== parentDetails.genderId
            }
          >
            {fetchedData?.genderId === GenderEnum.Male
              ? t("gender.Male")
              : t("gender.Female")}
          </FetchedDiffValue>
        </Col>
        <Col md={3}>
          <TextField
            label={t("person.birthplace")}
            value={parentDetails.cityOfBirth ?? ""}
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={
              canApply &&
              !!fetchedData &&
              fetchedData.cityOfBirth !== parentDetails.cityOfBirth
            }
          >
            {fetchedData?.cityOfBirth}
          </FetchedDiffValue>
        </Col>
        <Col md={3}>
          <TextField
            label={t("person.citizenship")}
            value={getCodebookName(citizenships, parentDetails.citizenshipId)}
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={
              canApply &&
              !!fetchedData &&
              fetchedData.citizenshipId !== parentDetails.citizenshipId
            }
          >
            {getCodebookName(citizenships, fetchedData?.citizenshipId)}
          </FetchedDiffValue>
        </Col>
        <Col md={3}>
          <TextField
            label={t("person.countryBirth")}
            value={getCodebookName(countries, parentDetails.countryOfBirthId)}
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={
              canApply &&
              !!fetchedData &&
              fetchedData.countryOfBirthId !== parentDetails.countryOfBirthId
            }
          >
            {getCodebookName(countries, fetchedData?.countryOfBirthId)}
          </FetchedDiffValue>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col md={3}>
          <Select
            label={t("person.parentshipType")}
            value={parentDetails.parentshipTypeId ?? null}
            selectData={{
              keyProp: "id",
              valueProp: "id",
              textProp: "name",
              data: parentshipTypes ?? [],
            }}
            onChange={(e: React.ChangeEvent<SelectEvent>) =>
              handleUpdate({ parentshipTypeId: Number(e.target.value) })
            }
            required
          />
        </Col>
        <Col md={3}>
          <TextField
            label={t("person.qualification")}
            value={parentDetails.qualificationName ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleUpdate({ qualificationName: e.target.value })
            }
            maxLength={255}
          />
        </Col>
        <Col md={3}>
          <TextField
            label={t("person.phoneNumber")}
            value={parentDetails.phoneNumber ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleUpdate({ phoneNumber: e.target.value })
            }
            required
            maxLength={30}
          />
        </Col>
        <Col md={3}>
          <TextField
            label={t("person.email")}
            value={parentDetails.email ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleUpdate({ email: e.target.value })
            }
            required
            maxLength={254}
          />
        </Col>
      </Row>
      <SectionHeader
        title={t("person.permanentResidenceInformation")}
        subtitle={t("person.mupSource")}
      />
      <AddressDisplay
        address={parentDetails.residence?.permanentAddress}
        fetchedAddress={fetchedData?.residence?.permanentAddress}
        canApply={canApply}
        title={t("person.permanentResidenceInformation")}
      />
      <SectionHeader
        title={t("person.temporaryResidenceInformation")}
        subtitle={t("person.mupSource")}
      />
      <AddressDisplay
        address={parentDetails.residence?.temporaryAddress}
        fetchedAddress={fetchedData?.residence?.temporaryAddress}
        canApply={canApply}
        title={t("person.temporaryResidenceInformation")}
      />
    </FormCard>
  );
};
